
import { useQuery } from '@tanstack/react-query';
import { Card, Heading, PageHeader, Stack, Text } from '@client/shared/design';
import { useCurrentUser } from '@client/shared/auth/index.ts';
import { fetchViewer, VIEWER_QUERY_KEY } from './viewer-operations.ts';
import { IdentityBlock } from './IdentityBlock.tsx';
import { NotificationsBlock } from './NotificationsBlock.tsx';
import { SecurityBlock } from './SecurityBlock.tsx';
import styles from './MyAccount.module.css';

export function MyAccount() {
  const sessionUser = useCurrentUser();

  const viewerQuery = useQuery({
    queryKey: VIEWER_QUERY_KEY,
    queryFn: ({ signal }) => fetchViewer(signal),
    staleTime: 30_000,
  });

  if (!sessionUser) return null;

  const viewer = viewerQuery.data;
  const name = viewer?.name ?? sessionUser.user.name;
  const email = viewer?.email ?? sessionUser.user.email;
  const roles = viewer?.roles ?? sessionUser.user.roles;

  return (
    <Stack gap={6} className={styles.page}>
      <PageHeader
        eyebrow="Your account"
        title="My account"
        description="Manage your name, email preferences, and password."
      />

      <IdentityBlock name={name} email={email} roles={roles} />

      {viewer ? (
        <NotificationsBlock settings={viewer.notificationSettings} />
      ) : (
        <Card pad="lg" spine as="section" aria-label="Email notifications">
          <Stack gap={1}>
            <Heading level={3} as="h2">
              Email notifications
            </Heading>
            {viewerQuery.isError ? (
              <Text size="sm" tone="danger" role="alert">
                Couldn’t load your notification preferences. Reload the page to try again.
              </Text>
            ) : (
              <Text size="sm" tone="muted">
                Loading your preferences…
              </Text>
            )}
          </Stack>
        </Card>
      )}

      <SecurityBlock email={email} />
    </Stack>
  );
}
